import { type ColumnDef } from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { DataTable } from "src/components/DataTable";
import { type DataTableFeatures } from "src/components/dataTableFeatures";
import { TableSearchProvider } from "src/components/TableSearchContext";
import {
  formatDistance,
  formatDuration,
  formatElevation,
  formatPace,
  formatPower,
  formatSpeed,
} from "src/lib/formatMetrics";

interface Lap {
  id: string;
  lapIndex: number;
  name: string;
  distance: number;
  elapsedTime: number;
  movingTime: number;
  totalElevationGain: number;
  averageSpeed: number;
  maxSpeed: number;
  averageCadence: number | null;
  averageWatts: number | null;
  startDate: string;
}

interface StravaActivityLapsProps {
  laps: Lap[];
  workoutType: string;
}

function getColumns(
  t: TFunction,
  workoutType: string,
): ColumnDef<DataTableFeatures, Lap>[] {
  const base: ColumnDef<DataTableFeatures, Lap>[] = [
    {
      id: "lapIndex",
      header: t("integrationsPage.stravaActivities.laps.index"),
      cell: ({ row }) => {
        return <span className="text-sm">{row.original.lapIndex}</span>;
      },
    },
    {
      id: "distance",
      header: t("integrationsPage.stravaActivities.laps.distance"),
      cell: ({ row }) => {
        return (
          <span className="text-sm">
            {formatDistance(row.original.distance) ?? "-"}
          </span>
        );
      },
    },
  ];

  if (workoutType === "CYCLING") {
    return [
      ...base,
      {
        id: "speed",
        header: t("integrationsPage.stravaActivities.laps.speed"),
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {formatSpeed(row.original.averageSpeed) ?? "-"}
            </span>
          );
        },
      },
      {
        id: "duration",
        header: t("integrationsPage.stravaActivities.laps.duration"),
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {formatDuration(row.original.movingTime) ?? "-"}
            </span>
          );
        },
      },
      {
        id: "power",
        header: t("integrationsPage.stravaActivities.laps.power"),
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {formatPower(row.original.averageWatts) ?? "-"}
            </span>
          );
        },
      },
      {
        id: "elevation",
        header: t("integrationsPage.stravaActivities.laps.elevation"),
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {formatElevation(row.original.totalElevationGain) ?? "-"}
            </span>
          );
        },
      },
    ];
  }

  if (
    workoutType === "RUNNING" ||
    workoutType === "WALKING" ||
    workoutType === "HIKING"
  ) {
    return [
      ...base,
      {
        id: "pace",
        header: t("integrationsPage.stravaActivities.laps.pace"),
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {formatPace(
                row.original.distance,
                row.original.movingTime,
                "km",
              ) ?? "-"}
            </span>
          );
        },
      },
      {
        id: "duration",
        header: t("integrationsPage.stravaActivities.laps.duration"),
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {formatDuration(row.original.movingTime) ?? "-"}
            </span>
          );
        },
      },
      {
        id: "elevation",
        header: t("integrationsPage.stravaActivities.laps.elevation"),
        cell: ({ row }) => {
          return (
            <span className="text-sm">
              {formatElevation(row.original.totalElevationGain) ?? "-"}
            </span>
          );
        },
      },
    ];
  }

  return [
    ...base,
    {
      id: "duration",
      header: t("integrationsPage.stravaActivities.laps.duration"),
      cell: ({ row }) => {
        return (
          <span className="text-sm">
            {formatDuration(row.original.movingTime) ?? "-"}
          </span>
        );
      },
    },
    {
      id: "elevation",
      header: t("integrationsPage.stravaActivities.laps.elevation"),
      cell: ({ row }) => {
        return (
          <span className="text-sm">
            {formatElevation(row.original.totalElevationGain) ?? "-"}
          </span>
        );
      },
    },
  ];
}

export function StravaActivityLaps(props: StravaActivityLapsProps) {
  const { t } = useTranslation();

  const columns = getColumns(t, props.workoutType);

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {t("integrationsPage.stravaActivities.laps.title")}
      </h4>
      <TableSearchProvider
        defaultPageSize={Number.MAX_SAFE_INTEGER}
        backend="state"
      >
        <DataTable
          columns={columns}
          data={props.laps}
          getRowId={(row) => {
            return row.id;
          }}
          hideToolbar
          hidePagination
        />
      </TableSearchProvider>
    </div>
  );
}
